import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ShareButtons from '../../components/ShareButtons';
import MedicalReviewByline from '../../components/MedicalReviewByline';
import useBlogViews from '../../hooks/useBlogViews';
import { getArticleBySlug, blogArticles } from '../../data/blogArticles';
import { Calendar, Clock, ArrowLeft, ArrowRight, ArrowDown, User, Eye, CheckCircle, ChevronDown } from 'lucide-react';

const COLOR = {
  cyan: { badge: 'bg-cyan-500/10 text-cyan-400', glow: 'bg-cyan-600/10', bullet: 'text-cyan-400', box: 'from-cyan-900/20 to-slate-900/20 border-cyan-500/20' },
  blue: { badge: 'bg-blue-500/10 text-blue-400', glow: 'bg-blue-600/10', bullet: 'text-blue-400', box: 'from-blue-900/20 to-slate-900/20 border-blue-500/20' },
  orange: { badge: 'bg-orange-500/10 text-orange-400', glow: 'bg-orange-600/10', bullet: 'text-orange-400', box: 'from-orange-900/20 to-slate-900/20 border-orange-500/20' },
  emerald: { badge: 'bg-emerald-500/10 text-emerald-400', glow: 'bg-emerald-600/10', bullet: 'text-emerald-400', box: 'from-emerald-900/20 to-slate-900/20 border-emerald-500/20' },
  violet: { badge: 'bg-violet-500/10 text-violet-400', glow: 'bg-violet-600/10', bullet: 'text-violet-400', box: 'from-violet-900/20 to-slate-900/20 border-violet-500/20' },
  amber: { badge: 'bg-amber-500/10 text-amber-400', glow: 'bg-amber-600/10', bullet: 'text-amber-400', box: 'from-amber-900/20 to-slate-900/20 border-amber-500/20' },
  yellow: { badge: 'bg-yellow-500/10 text-yellow-400', glow: 'bg-yellow-600/10', bullet: 'text-yellow-400', box: 'from-yellow-900/20 to-slate-900/20 border-yellow-500/20' },
};

const Block = ({ block, c }) => {
  if (block.t === 'h2') {
    return <h2 className="text-2xl font-bold text-white mt-12 mb-4">{block.x}</h2>;
  }
  if (block.t === 'p') {
    return <p className="text-slate-300 leading-relaxed mb-4">{block.x}</p>;
  }
  if (block.t === 'ul') {
    return (
      <ul className="space-y-2 text-slate-300 my-4">
        {block.x.map((item, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className={`${c.bullet} mt-1`}>•</span>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    );
  }
  if (block.t === 'box') {
    return (
      <div className={`bg-gradient-to-r ${c.box} border rounded-xl p-6 my-6`}>
        {block.title && <h4 className="text-lg font-semibold text-white mb-4">{block.title}</h4>}
        <ul className="space-y-2.5 text-slate-300">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3">
              <CheckCircle className={`w-5 h-5 ${c.bullet} mt-0.5 flex-shrink-0`} />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (block.t === 'links') {
    return (
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-6 my-6">
        {block.title && <h4 className="text-lg font-semibold text-white mb-3">{block.title}</h4>}
        <ul className="space-y-2.5">
          {block.x.map((lnk, i) => (
            <li key={i}>
              <Link to={lnk.to} className={`inline-flex items-start gap-2 ${c.bullet} hover:underline underline-offset-4 font-medium`}>
                <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                <span>{lnk.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  if (block.t === 'note') {
    return (
      <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 my-8">
        <p className="text-slate-400 text-sm">
          <strong className="text-slate-300">Disclaimer:</strong> {block.x}
        </p>
      </div>
    );
  }
  return null;
};

const DISCLAIMER = 'This content is for general educational and informational purposes only and does not replace personalized medical advice, diagnosis or treatment. Please consult a qualified healthcare professional regarding your individual health, medications and any changes to diet or exercise.';

const BlogArticleDynamic = () => {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);
  const { formattedViews } = useBlogViews(slug);
  const [openFaq, setOpenFaq] = useState(null);

  // Inject Article + BreadcrumbList JSON-LD reliably via document.head
  // (react-helmet-async does not render <script> tags dependably here).
  useEffect(() => {
    if (!article) return undefined;
    const url = `https://tcubefitness.com/blog/${article.slug}`;
    const graph = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          '@id': `${url}#article`,
          headline: article.title,
          description: article.excerpt,
          author: {
            '@type': 'Person',
            name: article.author,
            description: article.authorCredentials,
            worksFor: { '@type': 'Organization', name: 'TCUBE FITNESS' },
          },
          publisher: {
            '@type': 'Organization',
            name: 'TCUBE FITNESS',
            logo: {
              '@type': 'ImageObject',
              url: 'https://customer-assets.emergentagent.com/job_8b2c67d7-f3f8-4dd8-ad33-921e8e7f1a96/artifacts/s6b7c1yc_transprant.png',
            },
          },
          datePublished: article.datePublished,
          dateModified: article.datePublished,
          mainEntityOfPage: url,
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${url}#breadcrumb`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://tcubefitness.com/' },
            { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://tcubefitness.com/blog' },
            { '@type': 'ListItem', position: 3, name: article.title, item: url },
          ],
        },
      ],
    };
    const id = 'blog-article-schema';
    if (article.faqs && article.faqs.length) {
      graph['@graph'].push({
        '@type': 'FAQPage',
        '@id': `${url}#faq`,
        mainEntity: article.faqs.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    }
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('script');
      el.type = 'application/ld+json';
      el.id = id;
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(graph);
    return () => {
      const node = document.getElementById(id);
      if (node) node.remove();
    };
  }, [article]);

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  const c = COLOR[article.categoryColor] || COLOR.blue;
  const articleUrl = `https://tcubefitness.com/blog/${article.slug}`;
  const related = blogArticles.filter((a) => a.slug !== article.slug).slice(0, 2);

  return (
    <>
      <Helmet>
        <title>{article.title} | TCUBE FITNESS</title>
        <meta name="description" content={article.excerpt} />
        {article.keywords && <meta name="keywords" content={article.keywords} />}
        <link rel="canonical" href={articleUrl} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:type" content="article" />
      </Helmet>

      <div className="min-h-screen bg-slate-950">
        <Header />

        {/* Article Header */}
        <section className="pt-28 pb-12 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className={`absolute top-0 left-1/4 w-96 h-96 ${c.glow} rounded-full blur-3xl`}></div>
          </div>

          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl mx-auto">
              <Link to="/blog" className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-6 transition-colors">
                <ArrowLeft className="w-4 h-4" />
                Back to Blog
              </Link>

              <span className={`inline-flex items-center gap-1 px-3 py-1 ${c.badge} text-xs font-medium rounded-full mb-4`}>
                {article.category}
              </span>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-slate-400 text-sm mb-4">
                <span className="flex items-center gap-2"><User className="w-4 h-4" />{article.author}</span>
                <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{article.date}</span>
                <span className="flex items-center gap-2"><Clock className="w-4 h-4" />{article.readTime}</span>
                <span className="flex items-center gap-2"><Eye className="w-4 h-4" />{formattedViews} views</span>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-slate-500 text-sm">{article.authorCredentials}</p>
                <ShareButtons url={articleUrl} title={article.title} />
              </div>
              <MedicalReviewByline className="mt-4" />
              {article.faqs && article.faqs.length > 0 && (
                <a
                  href="#faqs"
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-400 hover:text-white transition-colors"
                  aria-label="Jump to frequently asked questions"
                  data-testid="jump-to-faqs-link"
                >
                  Jump to FAQs
                  <ArrowDown className={`w-4 h-4 ${c.bullet}`} aria-hidden="true" />
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="pb-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-3xl mx-auto">
              {article.blocks.map((block, i) => (
                <Block key={i} block={block} c={c} />
              ))}

              <Block block={{ t: 'note', x: DISCLAIMER }} c={c} />

              {/* CTA */}
              <div className="bg-gradient-to-r from-blue-900/30 to-cyan-900/30 rounded-xl p-6 border border-blue-500/20 my-8">
                <h4 className="text-lg font-semibold text-white mb-3">Want Personalized Guidance?</h4>
                <p className="text-slate-300 mb-4">
                  Our medical team can build a plan around your health, conditions and goals, not a generic template.
                </p>
                <Link
                  to="/book-consultation?from=blog-article"
                  className="inline-flex items-center gap-2 text-blue-400 font-medium hover:text-blue-300"
                >
                  Book a Free Consultation
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>

            {/* FAQ */}
            {article.faqs && article.faqs.length > 0 && (
              <div id="faqs" className="max-w-3xl mx-auto mt-16 scroll-mt-32" data-testid="article-faq-section">
                <h2 className="text-2xl font-bold text-white mb-8">Frequently Asked Questions</h2>
                <div className="space-y-4">
                  {article.faqs.map((f, idx) => (
                    <div key={idx} className="bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden">
                      <button
                        onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                        className="w-full flex items-center justify-between p-5 text-left"
                        data-testid={`faq-toggle-${idx}`}
                      >
                        <span className="text-white font-semibold pr-4">{f.q}</span>
                        <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform ${openFaq === idx ? `rotate-180 ${c.bullet}` : 'text-slate-500'}`} />
                      </button>
                      <div className={`overflow-hidden transition-all ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                        <p className="px-5 pb-5 text-slate-400 leading-relaxed">{f.a}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Explore related programs & tools (internal linking by topic) */}
            {(() => {
              const cat = (article.category || '').toLowerCase();
              let links;
              if (cat.includes('diabetes')) links = [
                { label: 'Doctor-Led Diabetes & Clinical Nutrition program', to: '/medical-nutrition-therapy' },
                { label: 'Diabetes diet for parents', to: '/diabetes-diet-for-parents' },
                { label: 'Full Body Health Analysis', to: '/health-analysis' },
                { label: 'TDEE calculator', to: '/calculators/tdee' },
              ];
              else if (cat.includes('weight') || cat.includes('metabolic')) links = [
                { label: 'Doctor-Led Fitness & weight management program', to: '/doctor-led-fitness' },
                { label: 'Clinical Nutrition for metabolic health', to: '/medical-nutrition-therapy' },
                { label: 'Calorie Deficit calculator', to: '/calculators/calorie-deficit' },
                { label: 'BMI calculator', to: '/calculators/bmi' },
                { label: 'Full Body Health Analysis', to: '/health-analysis' },
              ];
              else if (cat.includes('women') || cat.includes('pcos') || cat.includes('fertility') || cat.includes('pregnan') || cat.includes('postpartum')) links = [
                { label: "Women's & Reproductive Health hub", to: '/hub/womens-reproductive-health' },
                { label: 'PCOS (PMOS) Support & Diet program', to: '/pcos-diet-plan' },
                { label: 'Fertility Nutrition & Preconception Support', to: '/fertility-nutrition' },
                { label: 'Pregnancy Nutrition program', to: '/pregnancy-nutrition' },
              ];
              else links = [
                { label: 'Doctor-Led Online Fitness & Nutrition Coaching', to: '/doctor-led-fitness' },
                { label: 'Protein Intake calculator', to: '/calculators/protein-intake' },
                { label: 'TDEE calculator', to: '/calculators/tdee' },
                { label: 'Full Body Health Analysis', to: '/health-analysis' },
              ];
              return (
                <div className="max-w-3xl mx-auto mt-16 bg-slate-900/60 border border-slate-800 rounded-2xl p-6" data-testid="article-cluster-links">
                  <h3 className="text-lg font-semibold text-white mb-3">Related programs &amp; tools</h3>
                  <ul className="space-y-2.5">
                    {links.map((l, i) => (
                      <li key={i}>
                        <Link to={l.to} className={`inline-flex items-start gap-2 ${c.bullet} hover:underline underline-offset-4 font-medium`}>
                          <ArrowRight className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
                          <span>{l.label}</span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })()}

            {/* Related Articles */}
            <div className="max-w-3xl mx-auto mt-16">
              <h3 className="text-xl font-bold text-white mb-6">Related Articles</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {related.map((r) => {
                  const rc = COLOR[r.categoryColor] || COLOR.blue;
                  return (
                    <Link
                      key={r.slug}
                      to={`/blog/${r.slug}`}
                      className="bg-slate-900/50 rounded-xl p-4 border border-slate-800 hover:border-slate-700 transition-colors"
                    >
                      <span className={`text-sm font-medium ${rc.bullet}`}>{r.category}</span>
                      <p className="text-white font-medium mt-1">{r.title}</p>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default BlogArticleDynamic;
